import { useEffect, useRef, useState } from 'react';
import { conTrongHan, troiThucSu } from './hansudung';

/**
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * TẦNG 3 — NHỊP VẼ LẠI. Luật ở `hansudung.ts`; móc này chỉ nhớ mốc và đập nhịp.
 *
 * dev_device hỏi hai câu, và cả hai đều là câu về TẦNG NÀY, không phải về dữ liệu:
 *   (a) cái gì kích hoạt vẽ lại vào đúng giây hạn hết?
 *   (b) máy ngủ / tab ẩn 3 tiếng rồi quay lại thì sao?
 *
 * 🔴 (a) — BA nguồn nhịp, cố ý thừa, vì mỗi nguồn hỏng theo một kiểu khác nhau:
 *
 *     `setInterval` 1 giây     · nhịp thường. Tab NỀN bị bóp — đó là lý do phải có hai cái kia.
 *     `visibilitychange`       · quay lại tab ⇒ tính lại NGAY, không đợi nhịp sau.
 *     `pageshow` + `focus`     · quay lại từ bfcache / đổi cửa sổ — `visibilitychange` không
 *                                phải lúc nào cũng bắn ở hai đường ấy.
 *
 * ⚠ NHƯNG KHÔNG MỘT NGUỒN NÀO Ở TRÊN ĐƯỢC LÀM ĐIỀU KIỆN ĐÚNG ĐẮN. Nếu cả ba cùng câm, con
 *   số vẽ ra vẫn phải SAI VỀ PHÍA AN TOÀN — và nó làm được, vì phép tính lấy MAX của hai
 *   đồng hồ (xem `troiThucSu`): nhịp tới muộn bao nhiêu thì hiệu số cũng đã lớn bấy nhiêu.
 *   Nhịp chỉ quyết định NHANH HAY CHẬM, không quyết định ĐÚNG HAY SAI. Đó là chỗ tôi không
 *   muốn đặt niềm tin vào một cái `setInterval`.
 *
 * ⛔ (b) tôi CHƯA ĐO ĐƯỢC và không đoán — hai lần thử trong sandbox đều hỏng dụng cụ
 *   (headless không ẩn tab thật, container không treo máy). Xem `hansudung.ts`: thiết kế
 *   được chọn để đúng ở CẢ HAI khả năng, nên câu trả lời không còn là điều kiện tiên quyết.
 * ═══════════════════════════════════════════════════════════════════════════════════════
 */
export function useConTrongHan(hanConLaiMs: number | undefined | null, moc: unknown): boolean {
  /* Hai mốc, hai đồng hồ — chúng canh nhau. Xem `troiThucSu()`. */
  const mocDonDieu = useRef(0);
  const mocTuong = useRef(0);
  const mocCu = useRef<unknown>(undefined);
  const [, nhip] = useState(0);

  /*
   * ⚠ Đặt lại trong THÂN hàm, không trong `useEffect`: hiệu ứng chạy SAU lần vẽ đầu, nên
   *   một câu trả lời mới sẽ bị chấm bằng mốc CŨ trong đúng khung hình ấy — tức là một kết
   *   luận vừa về đã có thể hiện ra là "hết hạn". Một khung hình sai vẫn là sai.
   */
  if (mocCu.current !== moc) {
    mocCu.current = moc;
    mocDonDieu.current = performance.now();
    mocTuong.current = Date.now();
  }

  const daTroi = () => troiThucSu(
    performance.now() - mocDonDieu.current,
    Date.now() - mocTuong.current,
  );

  useEffect(() => {
    if (typeof hanConLaiMs !== 'number' || hanConLaiMs <= 0) return;

    const veLai = () => nhip((n) => n + 1);

    /*
     * ⛔ DỪNG HẲN KHI ĐÃ HẾT HẠN — và lần này mã LÀM đúng câu chú thích nói.
     *   Bản đầu tôi viết đúng câu này rồi để `setInterval` chạy mãi: một chú thích đứng thay
     *   cho thứ nó mô tả (L21), do chính tôi gây ra, ngay trong lần vá tầng 2.
     */
    const t = setInterval(() => {
      veLai();
      if (!conTrongHan(hanConLaiMs, daTroi())) clearInterval(t);
    }, 1_000);

    /*
     * Quay lại tab / cửa sổ ⇒ tính lại NGAY. Ba đường vì chúng không phủ nhau hoàn toàn.
     * ⚠ `visibilitychange` là thứ dev_device gợi ý — rẻ và đóng được cả (a) lẫn (b). Tôi đặt
     *   vào, nhưng KHÔNG dựa vào nó để đúng: xem khối đầu tệp.
     */
    document.addEventListener('visibilitychange', veLai);
    window.addEventListener('pageshow', veLai);
    window.addEventListener('focus', veLai);
    return () => {
      clearInterval(t);
      document.removeEventListener('visibilitychange', veLai);
      window.removeEventListener('pageshow', veLai);
      window.removeEventListener('focus', veLai);
    };
  }, [hanConLaiMs, moc]);

  return conTrongHan(hanConLaiMs, daTroi());
}
